import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, take } from 'rxjs';

/**
 * Coordinates the step-up (re-authentication) flow between the functional
 * `authInterceptor` — which cannot render UI — and the single re-auth modal
 * host mounted at the app root (`StepUpReauthComponent`).
 *
 * The interceptor calls `require()` when it sees a `403 { code:
 * STEP_UP_REQUIRED }`; that opens the modal (via `visible$`) and returns an
 * Observable that emits the flow's single outcome (true = re-authenticated,
 * false = cancelled). The modal host reports the outcome via `complete()`.
 *
 * Single-flight: concurrent `require()` calls (a burst of sensitive requests
 * all rejected at once) share ONE flow — one modal opens, and every waiting
 * caller receives the same outcome. This mirrors the 401 `refreshOutcome$`
 * single-flight discipline in `auth.interceptor.ts`.
 */
@Injectable({
  providedIn: 'root',
})
export class StepUpService {
  // Drives the modal host's `open` state. Starts hidden.
  private readonly _visible$ = new BehaviorSubject<boolean>(false);
  readonly visible$ = this._visible$.asObservable();

  // The current flow's outcome stream, or null when no step-up is in progress.
  // A ReplaySubject(1) so a caller that subscribes after the outcome is emitted
  // (ordering is not guaranteed across the async interceptor pipeline) still
  // receives it. Its presence is also the single-flight "in progress" flag.
  private outcome$: ReplaySubject<boolean> | null = null;

  /**
   * Signals that the current request needs step-up re-auth. The first caller
   * opens the modal; concurrent callers join the same in-flight flow. Returns
   * an Observable that emits exactly once with the flow's outcome.
   */
  require(): Observable<boolean> {
    if (!this.outcome$) {
      this.outcome$ = new ReplaySubject<boolean>(1);
      this._visible$.next(true);
    }
    return this.outcome$.pipe(take(1));
  }

  /**
   * Reports the outcome of the current flow and closes the modal. `true` after
   * a successful re-auth (waiting requests retry); `false` on cancel (they fail
   * as their original 403). No-op if no flow is in progress.
   */
  complete(success: boolean): void {
    const outcome$ = this.outcome$;
    // Clear the in-progress flag BEFORE emitting so a synchronous re-entrant
    // require() (e.g. a retried request that immediately 403s again) starts a
    // fresh flow rather than joining this one as it tears down.
    this.outcome$ = null;
    this._visible$.next(false);
    if (outcome$) {
      outcome$.next(success);
      outcome$.complete();
    }
  }
}
